import React, { useState } from 'react';
import { Flex, Box } from 'rebass';
import H1 from 'components/H1';
import cache from 'utils/cache';
import AppPageWrapper from 'components/AppPageWrapper';
import AppPageSubnav from 'components/AppPageWrapper/AppPageSubnav';
import AppPageBreadcrumbs from 'components/AppPageWrapper/AppPageBreadcrumbs';
import AppPageWrapperSectionHeader from 'components/AppPageWrapper/AppPageWrapperSectionHeader';
import AppPageWrapperSectionBody from 'components/AppPageWrapper/AppPageWrapperSectionBody';
import StudyPageSubNav from 'components/Study/StudyPageSubNav';
import { Helmet } from 'react-helmet';
import studies_service from 'services/studies';
import { useToasts } from 'react-toast-notifications';
import useQuery from 'hooks/useQuery';
import ItemWrapper from 'components/ItemWrapper';
import helpers from 'utils/helpers';

function StudyReportsPage(props) {
    const { addToast } = useToasts();

    const { data: study, isLoading } = useQuery({
        queryFn: async () => studies_service.getStudy(props.match.params.id),
        onError: error => {
            const errorText = services.parseAndTrackXhrErrors(error);
            addToast(errorText, {
                appearance: 'error',
                autoDismiss: true
            });
        }
    });

    const { data: reportingSummary, isLoading: isLoadingReportingSummary } = useQuery({
        queryFn: async () => studies_service.getStudyReportingSummary(props.match.params.id),
        onError: error => {
            const errorText = services.parseAndTrackXhrErrors(error);
            addToast(errorText, {
                appearance: 'error',
                autoDismiss: true
            });
        }
    });

    const renderScreenerReports = () => {
        if (reportingSummary.screeners.length == 0) {
            return <>No screeners</>;
        }

        let elements = [];
        elements.push(
            <ItemWrapper className="header-row" key={'reports-header'} style={{ padding: '16px 0' }}>
                <Box width={1 / 3}>Screener Title</Box>
                <Box width={2 / 3}>
                    <Flex>
                        <Box width={1 / 5}>Started</Box>
                        <Box width={1 / 5}>Completed</Box>
                        <Box width={1 / 5}>Qualified</Box>
                        <Box width={1 / 5}>Disqualified</Box>
                        <Box width={1 / 5}>Incomplete</Box>
                    </Flex>
                </Box>
            </ItemWrapper>
        );

        reportingSummary.screeners.forEach(screener => {
            elements.push(
                <ItemWrapper key={screener.id} padding={'16px 0'}>
                    <Box width={1 / 3} className="text-primary bold">
                        {screener.title}
                    </Box>
                    <Box width={2 / 3}>
                        <Flex>
                            <Box width={1 / 5}>
                                <span className="text-primary">{helpers.numberFormat(screener.all)}</span>
                            </Box>
                            <Box width={1 / 5}>
                                <span className="text-primary medium">{helpers.numberFormat(screener.complete)} </span>
                                <span className="fs-12 color-text-secondary">
                                    {' '}
                                    ({helpers.getPercentage(screener.complete, screener.all, 2)}%)
                                </span>
                            </Box>
                            <Box width={1 / 5}>
                                <span className="text-primary medium">{helpers.numberFormat(screener.qualified)} </span>
                                <span className="fs-12 color-text-secondary">
                                    {' '}
                                    ({helpers.getPercentage(screener.qualified, screener.all, 2)}%)
                                </span>
                            </Box>
                            <Box width={1 / 5}>
                                <span className="text-primary medium">
                                    {helpers.numberFormat(screener.disqualified)}{' '}
                                </span>
                                <span className="fs-12 color-text-secondary">
                                    {' '}
                                    ({helpers.getPercentage(screener.disqualified, screener.all, 2)}%)
                                </span>
                            </Box>
                            <Box width={1 / 5}>
                                <span className="text-primary medium">
                                    {helpers.numberFormat(screener.incomplete)}{' '}
                                </span>
                                <span className="fs-12 color-text-secondary">
                                    {' '}
                                    ({helpers.getPercentage(screener.incomplete, screener.all, 2)}%)
                                </span>
                            </Box>
                        </Flex>
                    </Box>
                </ItemWrapper>
            );
        });

        return elements;
    };

    return (
        <Flex style={{ width: '100%' }}>
            <AppPageWrapper>
                <Helmet>
                    <title>Reports Summary</title>
                </Helmet>

                <AppPageSubnav>
                    <AppPageBreadcrumbs
                        items={[
                            { url: `/studies`, title: `Studies` },
                            { url: `/studies/${props.match.params.id}`, title: study ? study.title : '' },
                            { url: null, title: `Reports Summary` }
                        ]}
                    ></AppPageBreadcrumbs>
                </AppPageSubnav>
                <Flex>
                    <StudyPageSubNav study_id={props.match.params.id} study={study} />
                </Flex>
                <AppPageWrapperSectionHeader className="experience">
                    <Box mt={3} className="text-primary fs-headline-20">
                        Participation Summary
                    </Box>
                </AppPageWrapperSectionHeader>
                <AppPageWrapperSectionBody>
                    <Box mx={4} my={3} width="100%">
                        {isLoadingReportingSummary || !reportingSummary ? (
                            <>Loading...</>
                        ) : (
                            <>
                                <Flex sx={{ gridGap: 4 }}>
                                    <Box className="basic-card" p={3} sx={{ width: '200px' }}>
                                        <Box className="color-text-secondary medium">Respondents</Box>
                                        <span className="fs-headline-24 color-text-primary">
                                            {helpers.numberFormat(reportingSummary.overview.all)}{' '}
                                        </span>
                                    </Box>
                                    <Box className="basic-card" p={3} sx={{ width: '200px' }}>
                                        <Box className="color-text-secondary medium">Invited</Box>
                                        <span className="fs-headline-24 color-text-primary">
                                            {helpers.numberFormat(reportingSummary.overview.invited)}{' '}
                                        </span>
                                        <span className="color-text-secondary">
                                            {' '}
                                            (
                                            {helpers.getPercentage(
                                                reportingSummary.overview.invited,
                                                reportingSummary.overview.all,
                                                2
                                            )}
                                            %)
                                        </span>
                                    </Box>
                                    <Box className="basic-card" p={3} sx={{ width: '200px' }}>
                                        <Box className="color-text-secondary medium">Screener Answered</Box>
                                        <span className="fs-headline-24 color-text-primary">
                                            {helpers.numberFormat(reportingSummary.overview.answered)}{' '}
                                        </span>
                                        <span className="color-text-secondary">
                                            {' '}
                                            (
                                            {helpers.getPercentage(
                                                reportingSummary.overview.answered,
                                                reportingSummary.overview.invited,
                                                2
                                            )}
                                            %)
                                        </span>
                                    </Box>
                                    <Box className="basic-card" p={3} sx={{ width: '200px' }}>
                                        <Box className="color-text-secondary medium">Scheduled</Box>
                                        <span className="fs-headline-24 color-text-primary">
                                            {helpers.numberFormat(reportingSummary.overview.scheduled)}{' '}
                                        </span>
                                        <span className="color-text-secondary">
                                            {' '}
                                            (
                                            {helpers.getPercentage(
                                                reportingSummary.overview.scheduled,
                                                reportingSummary.overview.invited,
                                                2
                                            )}
                                            %)
                                        </span>
                                    </Box>
                                    <Box className="basic-card" p={3} sx={{ width: '200px' }}>
                                        <Box className="color-text-secondary medium">Paid</Box>
                                        <span className="fs-headline-24 color-text-primary">
                                            {helpers.numberFormat(reportingSummary.overview.paid)}{' '}
                                        </span>
                                        <span className="color-text-secondary">
                                            {' '}
                                            (
                                            {helpers.getPercentage(
                                                reportingSummary.overview.paid,
                                                reportingSummary.overview.invited,
                                                2
                                            )}
                                            %)
                                        </span>
                                    </Box>
                                </Flex>

                                <Box mt={4} className="text-primary fs-headline-20">
                                    Email Summary
                                </Box>
                                <Flex sx={{ gap: 4 }} mt={3} mb={4}>
                                    <Box className="basic-card" p={3} sx={{ width: '200px' }}>
                                        <Box className="color-text-secondary medium">Sent</Box>
                                        <span className="fs-headline-24 color-text-primary">
                                            {helpers.numberFormat(reportingSummary.email_stats.sent)}{' '}
                                        </span>
                                    </Box>
                                    <Box className="basic-card" p={3} sx={{ width: '200px' }}>
                                        <Box className="color-text-secondary medium">Opened</Box>
                                        <span className="fs-headline-24 color-text-primary">
                                            {helpers.numberFormat(reportingSummary.email_stats.open)}{' '}
                                        </span>
                                        <span className="color-text-secondary">
                                            {' '}
                                            (
                                            {helpers.getPercentage(
                                                reportingSummary.email_stats.open,
                                                reportingSummary.email_stats.sent,
                                                2
                                            )}
                                            %)
                                        </span>
                                    </Box>
                                    <Box className="basic-card" p={3} sx={{ width: '200px' }}>
                                        <Box className="color-text-secondary medium">Bounced</Box>
                                        <span className="fs-headline-24 color-text-primary">
                                            {helpers.numberFormat(reportingSummary.email_stats.bounced)}{' '}
                                        </span>
                                        <span className="color-text-secondary">
                                            {' '}
                                            (
                                            {helpers.getPercentage(
                                                reportingSummary.email_stats.bounced,
                                                reportingSummary.email_stats.sent,
                                                2
                                            )}
                                            %)
                                        </span>
                                    </Box>
                                    <Box className="basic-card" p={3} sx={{ width: '200px' }}>
                                        <Box className="color-text-secondary medium">Spam</Box>
                                        <span className="fs-headline-24 color-text-primary">
                                            {helpers.numberFormat(reportingSummary.email_stats.complaint)}{' '}
                                        </span>
                                        <span className="color-text-secondary">
                                            {' '}
                                            (
                                            {helpers.getPercentage(
                                                reportingSummary.email_stats.complaint,
                                                reportingSummary.email_stats.sent,
                                                2
                                            )}
                                            %)
                                        </span>
                                    </Box>
                                    <Box className="basic-card" p={3} sx={{ width: '200px' }}>
                                        <Box className="color-text-secondary medium">Failed</Box>
                                        <span className="fs-headline-24 color-text-primary">
                                            {helpers.numberFormat(reportingSummary.email_stats.failed)}{' '}
                                        </span>
                                    </Box>
                                </Flex>

                                <Box mt={4} className="text-primary fs-headline-20">
                                    Screener Summary
                                </Box>
                                <Flex sx={{ gap: 4 }} mt={3} mb={4}>
                                    <Box className="basic-card" p={3} sx={{ width: '200px' }}>
                                        <Box className="color-text-secondary medium">Started</Box>
                                        <span className="fs-headline-24 color-text-primary">
                                            {helpers.numberFormat(reportingSummary.screeners_overview.all)}{' '}
                                        </span>
                                    </Box>
                                    <Box className="basic-card" p={3} sx={{ width: '200px' }}>
                                        <Box className="color-text-secondary medium">Completed</Box>
                                        <span className="fs-headline-24 color-text-primary">
                                            {helpers.numberFormat(reportingSummary.screeners_overview.complete)}{' '}
                                        </span>
                                        <span className="color-text-secondary">
                                            {' '}
                                            (
                                            {helpers.getPercentage(
                                                reportingSummary.screeners_overview.complete,
                                                reportingSummary.screeners_overview.all,
                                                2
                                            )}
                                            %)
                                        </span>
                                    </Box>
                                    <Box className="basic-card" p={3} sx={{ width: '200px' }}>
                                        <Box className="color-text-secondary medium">Qualified</Box>
                                        <span className="fs-headline-24 color-text-primary">
                                            {helpers.numberFormat(reportingSummary.screeners_overview.qualified)}{' '}
                                        </span>
                                        <span className="color-text-secondary">
                                            {' '}
                                            (
                                            {helpers.getPercentage(
                                                reportingSummary.screeners_overview.qualified,
                                                reportingSummary.screeners_overview.complete,
                                                2
                                            )}
                                            %)
                                        </span>
                                    </Box>
                                    <Box className="basic-card" p={3} sx={{ width: '200px' }}>
                                        <Box className="color-text-secondary medium">Disqualified</Box>
                                        <span className="fs-headline-24 color-text-primary">
                                            {helpers.numberFormat(reportingSummary.screeners_overview.disqualified)}{' '}
                                        </span>
                                        <span className="color-text-secondary">
                                            {' '}
                                            (
                                            {helpers.getPercentage(
                                                reportingSummary.screeners_overview.disqualified,
                                                reportingSummary.screeners_overview.complete,
                                                2
                                            )}
                                            %)
                                        </span>
                                    </Box>
                                    <Box className="basic-card" p={3} sx={{ width: '200px' }}>
                                        <Box className="color-text-secondary medium">Incomplete</Box>
                                        <span className="fs-headline-24 color-text-primary">
                                            {helpers.numberFormat(reportingSummary.screeners_overview.incomplete)}{' '}
                                        </span>
                                        <span className="color-text-secondary">
                                            {' '}
                                            (
                                            {helpers.getPercentage(
                                                reportingSummary.screeners_overview.incomplete,
                                                reportingSummary.screeners_overview.all,
                                                2
                                            )}
                                            %)
                                        </span>
                                    </Box>
                                </Flex>
                                {renderScreenerReports()}
                            </>
                        )}
                    </Box>
                </AppPageWrapperSectionBody>
            </AppPageWrapper>
        </Flex>
    );
}

export default StudyReportsPage;
